import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Clock, Download, Loader2, Users, Calendar, Search, Briefcase, Eye, Coffee, AlertCircle, Sigma,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ENTRY_LABELS = {
  clock_in:       { label: 'Arrivee',         color: 'bg-emerald-100 text-emerald-700' },
  clock_out:      { label: 'Depart',          color: 'bg-red-100 text-red-700' },
  mission_depart: { label: 'Depart mission',  color: 'bg-amber-100 text-amber-700' },
  mission_return: { label: 'Retour mission',  color: 'bg-blue-100 text-blue-700' },
  break_start:    { label: 'Debut de pause',  color: 'bg-indigo-100 text-indigo-700' },
  break_end:      { label: 'Fin de pause',    color: 'bg-indigo-50 text-indigo-600' },
  session_start:  { label: 'Connexion',       color: 'bg-gray-100 text-gray-600' },
  session_end:    { label: 'Deconnexion',     color: 'bg-gray-100 text-gray-600' },
};

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    opts.push({ value, label });
  }
  return opts;
}

function formatHours(decimal) {
  if (!decimal) return '0h00';
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return `${h}h${String(m).padStart(2, '0')}`;
}

function formatMinutes(min) {
  const v = Math.max(0, Math.floor(min || 0));
  return `${Math.floor(v / 60)}h${String(v % 60).padStart(2, '0')}`;
}

const TimeTracking = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchAgents = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get('/time-tracking/agents');
      setAgents(res.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const fetchDetail = async (agent) => {
    setSelectedAgent(agent);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const res = await api.get(`/time-tracking/report/${agent.id}`, { params: { month: selectedMonth } });
      setDetailData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
      setSelectedAgent(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownloadPDF = async (agent) => {
    try {
      const res = await api.get(`/time-tracking/report/${agent.id}/pdf`, {
        params: { month: selectedMonth },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `releve_heures_${(agent.name || 'agent').replace(/\s/g, '_')}_${selectedMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Releve telecharge');
    } catch {
      toast.error('Erreur de telechargement');
    }
  };

  const filteredAgents = useMemo(() => {
    return agents.filter(a => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return a.name?.toLowerCase().includes(s) || a.email?.toLowerCase().includes(s);
    });
  }, [agents, searchTerm]);

  const totals = useMemo(() => {
    return {
      totalAgents: agents.length,
      totalHours: agents.reduce((sum, a) => sum + (a.month_hours || 0), 0),
      totalMission: agents.reduce((sum, a) => sum + (a.month_mission_hours || 0), 0),
      totalNet: agents.reduce((sum, a) => sum + (a.month_net_hours || 0), 0),
      totalBreak: agents.reduce((sum, a) => sum + (a.month_break_hours || 0), 0),
      totalOvertime: agents.reduce((sum, a) => sum + (a.month_overtime_hours || 0), 0),
      totalDays: agents.reduce((sum, a) => sum + (a.days_worked || 0), 0),
    };
  }, [agents]);

  if (!isAdmin) {
    return (
      <div className="text-center py-20" data-testid="time-tracking-forbidden">
        <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Reserve aux administrateurs</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" /></div>;
  }

  const monthsOpts = monthOptions();
  const byDay = detailData?.stats?.by_day || {};
  const sortedDays = Object.keys(byDay).sort();

  return (
    <div className="space-y-6" data-testid="time-tracking-admin-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            <Clock className="w-7 h-7 text-[#1B3A4B]" />
            Releve d'Heures
          </h1>
          <p className="text-gray-500 mt-1">Suivi de presence, pauses et heures supplementaires de vos collaborateurs</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[200px]" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthsOpts.map(o => <SelectItem key={o.value} value={o.value} className="capitalize">{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Collaborateurs', value: totals.totalAgents, icon: Users, color: 'text-[#1B3A4B]', bg: 'bg-[#1B3A4B]/8' },
          { label: 'Presence', value: formatHours(totals.totalHours), icon: Clock, color: 'text-[#1B3A4B]', bg: 'bg-gray-100' },
          { label: 'Temps net', value: formatHours(totals.totalNet), icon: Sigma, color: 'text-emerald-700', bg: 'bg-emerald-50' },
          { label: 'Mission', value: formatHours(totals.totalMission), icon: Briefcase, color: 'text-amber-700', bg: 'bg-amber-50' },
          { label: 'Pauses', value: formatHours(totals.totalBreak), icon: Coffee, color: 'text-indigo-700', bg: 'bg-indigo-50' },
          { label: 'Heures sup', value: formatHours(totals.totalOvertime), icon: AlertCircle, color: 'text-[#9C7710]', bg: 'bg-[#D4A745]/15' },
        ].map((s, i) => (
          <Card key={i} className="hover:shadow-md transition-shadow">
            <CardContent className="p-3">
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
                  <s.icon className={`w-4.5 h-4.5 ${s.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{s.label}</p>
                  <p className={`text-base font-bold ${s.color} truncate tabular-nums`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>{s.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Rechercher un collaborateur..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-agents"
        />
      </div>

      {/* Agents list */}
      {filteredAgents.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Users className="w-14 h-14 mx-auto mb-3 opacity-40" />
          <p>Aucun collaborateur trouve</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredAgents.map(agent => (
            <Card key={agent.id} className="hover:shadow-md transition-shadow" data-testid={`agent-row-${agent.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1B3A4B] to-[#142D3A] text-[#D4A745] flex items-center justify-center flex-shrink-0 font-bold">
                      {agent.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 truncate">{agent.name}</span>
                        <Badge variant="outline" className="text-xs">{agent.role}</Badge>
                        {agent.employee_id && <span className="text-xs font-mono text-gray-500">{agent.employee_id}</span>}
                      </div>
                      <span className="text-xs text-gray-500">{agent.email}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 sm:gap-5 text-sm flex-wrap">
                    <div className="text-center min-w-[60px]">
                      <p className="font-bold text-[#1B3A4B] tabular-nums">{formatHours(agent.month_hours)}</p>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Presence</p>
                    </div>
                    <div className="text-center min-w-[60px]">
                      <p className="font-bold text-emerald-700 tabular-nums">{formatHours(agent.month_net_hours)}</p>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Net</p>
                    </div>
                    <div className="text-center min-w-[60px]">
                      <p className="font-bold text-indigo-700 tabular-nums">{formatHours(agent.month_break_hours)}</p>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Pause</p>
                    </div>
                    <div className="text-center min-w-[60px]">
                      <p className="font-bold text-amber-700 tabular-nums">{formatHours(agent.month_mission_hours)}</p>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Mission</p>
                    </div>
                    <div className="text-center min-w-[60px]">
                      <p className={`font-bold tabular-nums ${agent.month_overtime_hours > 0 ? 'text-[#9C7710]' : 'text-gray-400'}`}>
                        {formatHours(agent.month_overtime_hours)}
                      </p>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Sup</p>
                    </div>
                    <div className="text-center min-w-[40px]">
                      <p className="font-bold text-blue-600 tabular-nums">{agent.days_worked}</p>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Jours</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => fetchDetail(agent)} data-testid={`view-detail-${agent.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(agent)} data-testid={`download-pdf-${agent.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selectedAgent} onOpenChange={(o) => !o && setSelectedAgent(null)}>
        <DialogContent className="sm:max-w-[960px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#1B3A4B]" />
              Detail des pointages - {selectedAgent?.name}
            </DialogTitle>
            <DialogDescription>
              {monthsOpts.find(o => o.value === selectedMonth)?.label}
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : detailData ? (
            <div className="flex-1 overflow-y-auto min-h-0 space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-[#1B3A4B] tabular-nums">{formatMinutes(detailData.stats?.presence_minutes || 0)}</p>
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mt-1">Presence</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-emerald-700 tabular-nums">{formatMinutes(detailData.stats?.worked_minutes || 0)}</p>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mt-1">Temps net</p>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-indigo-700 tabular-nums">{formatMinutes(detailData.stats?.break_minutes || 0)}</p>
                  <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold mt-1">Pauses</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-amber-700 tabular-nums">{formatMinutes(detailData.stats?.mission_minutes || 0)}</p>
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mt-1">Mission</p>
                </div>
                <div className={`border rounded-lg p-3 text-center ${detailData.stats?.overtime_minutes > 0 ? 'bg-[#D4A745]/15 border-[#D4A745]/40' : 'bg-gray-50 border-gray-200'}`}>
                  <p className={`text-xl font-bold tabular-nums ${detailData.stats?.overtime_minutes > 0 ? 'text-[#9C7710]' : 'text-gray-400'}`}>
                    {formatMinutes(detailData.stats?.overtime_minutes || 0)}
                  </p>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold mt-1 ${detailData.stats?.overtime_minutes > 0 ? 'text-[#9C7710]' : 'text-gray-500'}`}>
                    Heures sup
                  </p>
                </div>
              </div>

              {/* Daily breakdown table */}
              {sortedDays.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Detail par jour
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-[#1B3A4B] text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-center">Arrivee</th>
                        <th className="px-3 py-2 text-center">Depart</th>
                        <th className="px-3 py-2 text-center">Presence</th>
                        <th className="px-3 py-2 text-center">Pause</th>
                        <th className="px-3 py-2 text-center">Net</th>
                        <th className="px-3 py-2 text-center">H. sup</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDays.map((dayKey) => {
                        const d = byDay[dayKey];
                        const date = new Date(dayKey);
                        return (
                          <tr key={dayKey} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-xs">
                              {date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                            </td>
                            <td className="px-3 py-2 text-center font-mono text-xs">
                              {d.arrival ? new Date(d.arrival).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                            <td className="px-3 py-2 text-center font-mono text-xs">
                              {d.departure ? new Date(d.departure).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                            <td className="px-3 py-2 text-center text-[#1B3A4B] font-semibold tabular-nums">{formatMinutes(d.presence_minutes)}</td>
                            <td className="px-3 py-2 text-center text-indigo-700 tabular-nums">{formatMinutes(d.break_minutes)}</td>
                            <td className="px-3 py-2 text-center text-emerald-700 font-semibold tabular-nums">{formatMinutes(d.worked_minutes)}</td>
                            <td className={`px-3 py-2 text-center font-semibold tabular-nums ${d.overtime_minutes > 0 ? 'text-[#9C7710]' : 'text-gray-400'}`}>
                              {formatMinutes(d.overtime_minutes)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Raw entries timeline */}
              {detailData.entries?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Historique des pointages
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left">Date / Heure</th>
                        <th className="px-3 py-2 text-left">Action</th>
                        <th className="px-3 py-2 text-left">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailData.entries.map((e) => {
                        const cfg = ENTRY_LABELS[e.entry_type] || { label: e.entry_type, color: 'bg-gray-100' };
                        const dt = new Date(e.timestamp);
                        return (
                          <tr key={e.id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-xs">
                              {dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              {' '}
                              <span className="text-[#1B3A4B] font-bold">
                                {dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <Badge className={cfg.color + ' border-0'}>{cfg.label}</Badge>
                            </td>
                            <td className="px-3 py-2 text-gray-600 text-xs">{e.notes || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {detailData.entries?.length === 0 && sortedDays.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  <p>Aucun pointage sur ce mois</p>
                </div>
              )}
            </div>
          ) : null}

          <div className="pt-3 border-t flex justify-end gap-2 flex-shrink-0">
            {selectedAgent && (
              <Button onClick={() => handleDownloadPDF(selectedAgent)} className="bg-[#1B3A4B] hover:bg-[#142D3A]">
                <Download className="w-4 h-4 mr-2" /> Telecharger releve PDF
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeTracking;
